import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Button, ButtonGroup, Form } from "@themesberg/react-bootstrap";
import Chartist from "react-chartist";
import { TrendPlot } from "../components/QuotesTrendChart";
import ChartistTooltip from "chartist-plugin-tooltips-updated";
import axios from "axios";
import { API_URL, END_POINT_ADMIN, END_POINT_COMPANY, END_POINT_REPORTS } from "../const";

const PERIODS = [
  { key: "3m", label: "3M" },
  { key: "6m", label: "6M" },
  { key: "12m", label: "12M" },
  { key: "all", label: "All" },
];

const STATUSES = ["Live", "Won", "Closed", "Lost"];
const VIEW_ALL_ROLES = ["admin", "super admin", "internal sales"];
const canViewAllQuotes = (roleName = "") =>
  VIEW_ALL_ROLES.includes(String(roleName).toLowerCase());

const STATUS_TONES = {
  Live: "live",
  Won: "won",
  Closed: "closed",
  Lost: "lost",
};

const emptyData = {
  kpis: {
    total_quotes: 0,
    total_amount: 0,
    won_quotes: 0,
    won_amount: 0,
    lost_quotes: 0,
    live_quotes: 0,
    live_amount: 0,
    win_rate: 0,
    po_count: 0,
    po_amount: 0,
    avg_quote_value: 0,
  },
  by_status: [],
  by_company: [],
  by_user: [],
  by_principal: [],
  by_branch: [],
  monthly: [],
};

const formatAmount = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

const formatCompactAmount = (value) => {
  const amount = Number(value) || 0;
  const withSuffix = (n, suffix) => `${String(Number(n.toFixed(1))).replace(/\.0$/, "")}${suffix}`;
  if (Math.abs(amount) >= 10000000) return withSuffix(amount / 10000000, "Cr");
  if (Math.abs(amount) >= 100000) return withSuffix(amount / 100000, "L");
  if (Math.abs(amount) >= 1000) return withSuffix(amount / 1000, "k");
  return `${Math.round(amount)}`;
};

const RankingList = ({ items, emptyLabel }) => {
  const maxAmount = Math.max(...items.map((item) => Number(item.amount) || 0), 1);

  if (!items.length) {
    return <p className="reports-empty mb-0">{emptyLabel}</p>;
  }

  return (
    <div className="reports-rank-list">
      {items.map((item, index) => (
        <div key={`${item.name}-${index}`} className="reports-rank-item">
          <div className="reports-rank-item__top">
            <span className="reports-rank-item__name">
              <span className="reports-rank-item__index">{index + 1}</span>
              {item.name}
            </span>
            <span className="reports-rank-item__amount">{formatCompactAmount(item.amount)}</span>
          </div>
          <div className="reports-rank-track">
            <div
              className="reports-rank-fill"
              style={{ width: `${Math.round((Number(item.amount) / maxAmount) * 100)}%` }}
            />
          </div>
          <div className="reports-rank-item__meta">{item.quotes} quotes</div>
        </div>
      ))}
    </div>
  );
};

export default function Reports() {
  const [period, setPeriod] = useState("12m");
  const [userId, setUserId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [status, setStatus] = useState("");
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(emptyData);

  const rawToken = sessionStorage.getItem("quotetoken");
  const token = rawToken?.replace(/^"|"$/g, "");
  const userDetail = JSON.parse(sessionStorage.getItem("quoteadmin") || "null");
  const showUserFilter = canViewAllQuotes(userDetail?.role_name);

  useEffect(() => {
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };

    const loadOptions = async () => {
      try {
        const [companyResponse, adminResponse] = await Promise.all([
          axios.get(API_URL + END_POINT_COMPANY, { headers }),
          showUserFilter ? axios.get(API_URL + END_POINT_ADMIN, { headers }) : Promise.resolve(null),
        ]);

        if (companyResponse.data?.status && Array.isArray(companyResponse.data.data)) {
          setCompanies(companyResponse.data.data.filter((company) => !company.deleted_at));
        }

        if (adminResponse?.data?.status && Array.isArray(adminResponse.data.data)) {
          setUsers(adminResponse.data.data);
        }
      } catch (error) {
        console.error("Error fetching report filters:", error);
      }
    };

    loadOptions();
  }, [token, showUserFilter]);

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}${END_POINT_REPORTS}`, {
          params: {
            period,
            ...(showUserFilter && userId ? { se_id: userId } : {}),
            ...(companyId ? { company_id: companyId } : {}),
            ...(status ? { status } : {}),
          },
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data?.status && response.data.data) {
          setData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching reports:", error);
        setData(emptyData);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [period, token, userId, companyId, status, showUserFilter]);

  const statusSeries = useMemo(
    () => data.by_status.map((item) => item.quotes),
    [data.by_status]
  );

  const monthlyAverage = useMemo(
    () => (data.monthly.length
      ? data.monthly.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) / data.monthly.length
      : 0),
    [data.monthly]
  );

  const outcomeChart = useMemo(() => ({
    labels: data.monthly.map((item) => item.label.replace(" 20", " '")),
    series: [
      data.monthly.map((item) => Number(item.won_quotes) || 0),
      data.monthly.map((item) => Number(item.lost_quotes) || 0),
    ],
  }), [data.monthly]);

  const kpis = [
    { label: "Total quotes", value: data.kpis.total_quotes, hint: "All quotes in this period" },
    { label: "Quote value", value: `₹ ${formatCompactAmount(data.kpis.total_amount)}`, hint: formatAmount(data.kpis.total_amount) },
    { label: "Won value", value: `₹ ${formatCompactAmount(data.kpis.won_amount)}`, hint: `${data.kpis.won_quotes} won · ${formatAmount(data.kpis.won_amount)}` },
    { label: "Win rate", value: `${data.kpis.win_rate}%`, hint: "Won vs lost quotes" },
    { label: "Live pipeline", value: `₹ ${formatCompactAmount(data.kpis.live_amount)}`, hint: `${data.kpis.live_quotes} live · ${formatAmount(data.kpis.live_amount)}` },
    { label: "PO sales", value: `₹ ${formatCompactAmount(data.kpis.po_amount)}`, hint: `${data.kpis.po_count} converted POs` },
  ];

  return (
    <div className="reports-page">
      <div className="dashboard-header">
        <div>
          <p className="dashboard-eyebrow mb-1">Analytics</p>
          <h2 className="dashboard-title mb-1">Reports</h2>
          <p className="dashboard-subtitle mb-0">
            See quote pipeline, wins, and value by company, user, and principal.
          </p>
        </div>
        <ButtonGroup className="quotes-trend-toggle">
          {PERIODS.map(({ key, label }) => (
            <Button
              key={key}
              className={period === key ? "active" : ""}
              onClick={() => setPeriod(key)}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </div>

      <div className="quotes-trend-panel__filters reports-filters mb-4">
        {showUserFilter && (
          <Form.Select value={userId} onChange={(event) => setUserId(event.target.value)} aria-label="Filter by user">
            <option value="">All users</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </Form.Select>
        )}
        <Form.Select value={companyId} onChange={(event) => setCompanyId(event.target.value)} aria-label="Filter by company">
          <option value="">All companies</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>{company.name}</option>
          ))}
        </Form.Select>
        <Form.Select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          {STATUSES.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </Form.Select>
      </div>

      {loading ? (
        <div className="reports-empty">Loading reports...</div>
      ) : (
        <>
          <Row className="g-3 mb-4">
            {kpis.map((kpi) => (
              <Col key={kpi.label} xs={12} sm={6} lg={4} xl={4} xxl={2}>
                <Card className="dashboard-panel reports-kpi h-100">
                  <Card.Body>
                    <p className="reports-kpi__label">{kpi.label}</p>
                    <h4 className="reports-kpi__value">{kpi.value}</h4>
                    <p className="reports-kpi__hint mb-0">{kpi.hint}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className="g-4 mb-4">
            <Col xs={12}>
              <Card className="dashboard-panel">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Quoted value over time</h5>
                    <p className="dashboard-panel__subtitle mb-0">Month-wise business volume</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  {data.monthly.length > 0 ? (
                    <TrendPlot key={period} series={data.monthly} metric="amount" view="bar"
                      average={monthlyAverage} monthly />
                  ) : (
                    <p className="reports-empty">No quote data for this period.</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            {data.by_status.map((item) => {
              const total = data.kpis.total_quotes || 1;
              const percent = Math.round((item.quotes / total) * 100);
              return (
                <Col key={item.status} xs={6} lg={3}>
                  <div className={`reports-pipeline reports-pipeline--${STATUS_TONES[item.status]}`}>
                    <span className="reports-pipeline__label">{item.status}</span>
                    <strong className="reports-pipeline__value">{item.quotes}</strong>
                    <span className="reports-pipeline__meta">{percent}% · {formatCompactAmount(item.amount)}</span>
                  </div>
                </Col>
              );
            })}
          </Row>

          <Row className="g-4 mb-4">
            <Col xs={12} xl={4}>
              <Card className="dashboard-panel h-100">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Quote status</h5>
                    <p className="dashboard-panel__subtitle mb-0">Where the pipeline stands</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  {statusSeries.some((value) => value > 0) ? (
                    <Chartist
                      data={{ series: statusSeries }}
                      options={{
                        donut: true,
                        donutWidth: 28,
                        showLabel: false,
                        plugins: [ChartistTooltip()],
                      }}
                      type="Pie"
                      className="reports-pie"
                    />
                  ) : (
                    <p className="reports-empty">No status data.</p>
                  )}
                  <div className="reports-status-legend">
                    {data.by_status.map((item) => (
                      <div key={item.status} className={`reports-status-legend__item is-${STATUS_TONES[item.status]}`}>
                        <span>{item.status}</span>
                        <strong>{item.quotes}</strong>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} xl={8}>
              <Card className="dashboard-panel h-100">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Won vs lost</h5>
                    <p className="dashboard-panel__subtitle mb-0">Conversion progress by month</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Chartist
                    data={outcomeChart}
                    options={{
                      low: 0,
                      height: 240,
                      fullWidth: true,
                      chartPadding: { top: 12, right: 12, left: 8, bottom: 8 },
                      axisX: { showGrid: false },
                      axisY: { onlyInteger: true, offset: 28 },
                      plugins: [ChartistTooltip()],
                    }}
                    type="Bar"
                    className="reports-outcome-chart"
                  />
                  <div className="reports-status-legend mt-3">
                    <div className="reports-status-legend__item is-won"><span>Won</span></div>
                    <div className="reports-status-legend__item is-lost"><span>Lost</span></div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4 mb-4">
            <Col xs={12} xl={6}>
              <Card className="dashboard-panel h-100">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Value by company</h5>
                    <p className="dashboard-panel__subtitle mb-0">Which companies contribute most</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <RankingList items={data.by_company} emptyLabel="No company totals yet." />
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} xl={6}>
              <Card className="dashboard-panel h-100">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Value by branch</h5>
                    <p className="dashboard-panel__subtitle mb-0">Office-wise contribution</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <RankingList items={data.by_branch || []} emptyLabel="No branch totals yet." />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4">
            <Col xs={12} xl={6}>
              <Card className="dashboard-panel h-100">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Value by user</h5>
                    <p className="dashboard-panel__subtitle mb-0">Sales executive contribution</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <RankingList items={data.by_user} emptyLabel="No user totals yet." />
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} xl={6}>
              <Card className="dashboard-panel h-100">
                <Card.Header className="dashboard-panel__header">
                  <div>
                    <h5 className="dashboard-panel__title mb-1">Value by principal</h5>
                    <p className="dashboard-panel__subtitle mb-0">Brand / line mix</p>
                  </div>
                </Card.Header>
                <Card.Body>
                  <RankingList items={data.by_principal} emptyLabel="No principal totals yet." />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
